<form [formGroup]="typeForm" class="atp">
  <header class="atp-head">
    <button type="button" class="atp-close" (click)="cancel()" aria-label="Close">
      <i class="bx bx-x" aria-hidden="true"></i>
    </button>
    <h2 class="atp-title">
      @if (type) {
        Edit {{ type.name }}
      } @else {
        Add New Type
      }
    </h2>
    <div class="atp-actions">
      <button type="button" class="atp-cancel" (click)="cancel()">Cancel</button>
      <button
        type="button"
        class="atp-save"
        [disabled]="typeForm.invalid || isExiting || isPosManagedEdit"
        (click)="save()"
      >
        Save Type
      </button>
    </div>
  </header>

  <div class="atp-body">
    <!-- Basic information -->
    <section class="atp-card">
      <header class="atp-card-head">
        <span class="atp-card-icon"><i class="bx bx-file" aria-hidden="true"></i></span>
        <h3 class="atp-card-title">Basic Information</h3>
      </header>

      <label class="atp-label" for="typeName">Type Name *</label>
      <input
        id="typeName"
        class="atp-input"
        type="text"
        autocomplete="off"
        placeholder="e.g. Cuisine"
        formControlName="typeName"
        (input)="filterType()"
        [class.input-error]="
          isExiting || (typeForm.get('typeName')?.invalid && typeForm.get('typeName')?.touched)
        "
      />
      @if (typeForm.get('typeName')?.touched && typeForm.get('typeName')?.errors?.['required']) {
        <div class="d-flex mt-2 errorText gap-2">
          <mat-icon svgIcon="warning-icon" class="warning-icon"></mat-icon>
          <p>Type name cannot be empty.</p>
        </div>
      }
      @if (typeForm.get('typeName')?.touched && typeForm.get('typeName')?.errors?.['minlength']) {
        <div class="d-flex mt-2 errorText gap-2">
          <mat-icon svgIcon="warning-icon" class="warning-icon"></mat-icon>
          <p>Type name must be at least 3 characters.</p>
        </div>
      }
      @if (isExiting) {
        <div class="d-flex mt-2 errorText gap-2">
          <mat-icon svgIcon="warning-icon" class="warning-icon"></mat-icon>
          <p>Type name is already existing.</p>
        </div>
      }

      <label class="atp-label atp-label--spaced" for="typeDescription">Description</label>
      <textarea
        id="typeDescription"
        class="atp-input atp-textarea"
        rows="3"
        placeholder="Enter description..."
        formControlName="description"
      ></textarea>
    </section>

    <!-- Settings -->
    <section class="atp-card">
      <header class="atp-card-head">
        <span class="atp-card-icon"><i class="bx bx-cog" aria-hidden="true"></i></span>
        <h3 class="atp-card-title">Settings</h3>
      </header>

      <div class="atp-setting-row">
        <div>
          <p class="atp-setting-title">Is Mandatory</p>
          <p class="atp-setting-sub">Customers must select at least one category under this type</p>
        </div>
        <div class="form-check form-switch atp-switch">
          <input
            class="form-check-input"
            type="checkbox"
            role="switch"
            id="isMandatory"
            formControlName="isMandatory"
            aria-label="Is mandatory"
          />
        </div>
      </div>

      <span class="atp-label atp-label--spaced">Selection Type</span>
      <div class="atp-choices">
        <button
          type="button"
          class="atp-choice"
          [class.atp-choice--active]="!typeForm.get('isMultiSelect')?.value"
          [disabled]="isPosManagedEdit"
          (click)="setSelection(false)"
        >
          <span class="atp-radio" aria-hidden="true"></span>
          <span>
            <span class="atp-choice-title">Single Select</span>
            <span class="atp-choice-sub">Only one category can be active</span>
          </span>
        </button>
        <button
          type="button"
          class="atp-choice"
          [class.atp-choice--active]="typeForm.get('isMultiSelect')?.value"
          [disabled]="isPosManagedEdit"
          (click)="setSelection(true)"
        >
          <span class="atp-radio" aria-hidden="true"></span>
          <span>
            <span class="atp-choice-title">Multi Select</span>
            <span class="atp-choice-sub">Multiple categories can be active</span>
          </span>
        </button>
      </div>

      <div class="atp-setting-row atp-setting-row--spaced">
        <div>
          <p class="atp-setting-title">Set as Default</p>
          <p class="atp-setting-sub">Always display this type at the top of filter lists</p>
        </div>
        <div class="form-check form-switch atp-switch">
          <input
            class="form-check-input"
            type="checkbox"
            role="switch"
            id="isDefault"
            formControlName="isDefault"
            aria-label="Set as default"
            (change)="onDefaultChange($any($event.target).checked)"
          />
        </div>
      </div>
    </section>

    <!-- Categories -->
    <section class="atp-card" formArrayName="categories">
      <header class="atp-card-head">
        <span class="atp-card-icon"><i class="bx bx-grid-alt" aria-hidden="true"></i></span>
        <h3 class="atp-card-title">Categories</h3>
        @if (type && !addingCategory && !isPosManagedEdit) {
          <button type="button" class="atp-add-category" (click)="startAddCategory()">
            <i class="bx bx-plus" aria-hidden="true"></i> Add Category
          </button>
        }
      </header>

      @for (control of categories.controls; track $index; let i = $index) {
        <div
          class="atp-cat-row"
          [class.atp-cat-row--editing]="editingIndex === i"
          [formGroupName]="i"
        >
          @if (editingIndex === i) {
            <input
              class="atp-input atp-cat-input"
              type="text"
              formControlName="name"
              (keyup.enter)="saveEditCategory()"
              (keyup.escape)="cancelEditCategory()"
              [class.input-error]="!!editingError"
            />
            <span class="atp-cat-actions">
              <button type="button" class="atp-row-save" (click)="saveEditCategory()">Save</button>
              <button type="button" class="atp-row-cancel" (click)="cancelEditCategory()">
                Cancel
              </button>
            </span>
          } @else {
            <span class="atp-cat-name">{{ control.value.name }}</span>
            <span class="atp-cat-actions">
              <button
                type="button"
                class="atp-icon-btn"
                aria-label="Rename category"
                [disabled]="isPosManagedEdit"
                (click)="startEditCategory(i)"
              >
                <i class="bx bx-edit-alt"></i>
              </button>
              <button
                type="button"
                class="atp-icon-btn atp-icon-btn--danger"
                aria-label="Remove category"
                [disabled]="isPosManagedEdit"
                (click)="removeCategory(i)"
              >
                <i class="bx bx-trash"></i>
              </button>
            </span>
          }
        </div>
        @if (editingIndex === i && editingError) {
          <div class="d-flex mb-2 errorText gap-2">
            <mat-icon svgIcon="warning-icon" class="warning-icon"></mat-icon>
            <p>{{ editingError }}</p>
          </div>
        }
      }

      @if (categories.errors?.['duplicate']) {
        <div class="d-flex mt-2 errorText gap-2">
          <mat-icon svgIcon="warning-icon" class="warning-icon"></mat-icon>
          <p>Category names must be unique.</p>
        </div>
      }

      @if (addingCategory) {
        <div class="atp-new">
          <span class="atp-eyebrow">New category</span>
          <div class="atp-new-row">
            <input
              class="atp-input"
              type="text"
              placeholder="e.g. Italian"
              [(ngModel)]="newCategoryName"
              [ngModelOptions]="{ standalone: true }"
              (keyup.enter)="confirmAddCategory()"
              [class.input-error]="!!newCategoryError"
            />
            <button type="button" class="atp-new-add" (click)="confirmAddCategory()">Add</button>
            <button type="button" class="atp-new-cancel" (click)="cancelAddCategory()">
              Cancel
            </button>
          </div>
          @if (newCategoryError) {
            <div class="d-flex mt-2 errorText gap-2">
              <mat-icon svgIcon="warning-icon" class="warning-icon"></mat-icon>
              <p>{{ newCategoryError }}</p>
            </div>
          }
        </div>
      } @else if (!type) {
        <button
          type="button"
          class="atp-add-category atp-add-category--block"
          (click)="startAddCategory()"
        >
          <i class="bx bx-plus" aria-hidden="true"></i> Add Category
        </button>
      }
    </section>
  </div>
</form>
